/**
 * Field formatting for the generic report viewer.
 *
 * The 17 backend report generators each return their own flat row shape, so the
 * viewer works out what a column is from its name rather than from a per-report
 * config. The rules live here so the on-screen table and the PDF export label
 * and format a column identically.
 */

const ACRONYMS = { id: 'ID', utm: 'UTM', wa: 'WhatsApp', gst: 'GST', vat: 'VAT', avg: 'Avg' };

/** 'total_invoice_amount' -> 'Total Invoice Amount' */
export const humanizeKey = (key) => String(key || '')
  .replace(/_/g, ' ')
  .trim()
  .split(' ')
  .filter(Boolean)
  .map((word) => ACRONYMS[word.toLowerCase()] || word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

// Money-ish names. Anchored at the end of the key so 'total_quotes' stays a
// count while 'quote_total' and 'total_quote_value' are money.
const CURRENCY_PATTERN = /(amount|revenue|value|salary|price|balance|outstanding|tax|total)$/i;
// ...unless the name says it counts something else
const NOT_CURRENCY_PATTERN = /(count|days|hours|rate|percentage|percent|number|quotes|invoices|leads|employees|reps|records)$/i;
const PERCENT_PATTERN = /(percentage|percent|_rate|^rate)$/i;
const DATE_PATTERN = /(date|_at)$/i;

/** Work out how a value should be rendered from its key and its type. */
export const detectFormat = (key, value) => {
  const name = String(key || '');

  if (typeof value === 'boolean') return 'boolean';
  if (PERCENT_PATTERN.test(name)) return 'percent';
  if (DATE_PATTERN.test(name)) return 'date';
  if (typeof value === 'number') {
    if (NOT_CURRENCY_PATTERN.test(name)) return 'number';
    if (CURRENCY_PATTERN.test(name)) return 'currency';
    return 'number';
  }
  return 'text';
};

export const formatValue = (value, format) => {
  if (value === null || value === undefined || value === '') return '-';

  switch (format) {
    case 'currency':
      return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    case 'percent':
      return `${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}%`;
    case 'number':
      return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    case 'boolean':
      return value ? 'Yes' : 'No';
    default:
      return String(value);
  }
};

/** Columns a row list should be shown with, ignoring ids and nested payloads. */
export const deriveColumns = (rows = [], { sampleSize = 25 } = {}) => {
  const sample = rows.slice(0, sampleSize);
  const keys = [];

  sample.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!keys.includes(key)) keys.push(key);
    });
  });

  return keys
    // Ids are for linking, not for reading; nested lists get their own handling
    .filter((key) => !/_id$/i.test(key) && key !== 'id')
    .filter((key) => !sample.some((row) => Array.isArray(row?.[key]) || (row?.[key] && typeof row[key] === 'object')))
    .map((key) => {
      const firstValue = sample.map((row) => row?.[key]).find((value) => value !== null && value !== undefined);
      const format = detectFormat(key, firstValue);
      return {
        key,
        title: humanizeKey(key),
        format,
        align: format === 'currency' || format === 'number' || format === 'percent' ? 'right' : 'left',
      };
    });
};

/** Summary entries that belong in a stat tile (one value, not a breakdown). */
export const scalarSummaryEntries = (summary = {}) => Object.entries(summary || {})
  .filter(([, value]) => value === null || ['string', 'number', 'boolean'].includes(typeof value))
  .map(([key, value]) => ({
    key,
    label: humanizeKey(key),
    value: formatValue(value, detectFormat(key, value)),
    raw: value,
  }));

/**
 * Summary entries that are a {label: number} breakdown - aging buckets, status
 * counts, tax by rate - which is what the viewer draws its charts from.
 */
export const breakdownSummaryEntries = (summary = {}) => Object.entries(summary || {})
  .filter(([, value]) => value
    && typeof value === 'object'
    && !Array.isArray(value)
    && Object.keys(value).length > 0
    && Object.values(value).every((entry) => typeof entry === 'number'))
  .map(([key, value]) => ({
    key,
    label: humanizeKey(key),
    format: detectFormat(key, Object.values(value)[0]),
    data: Object.entries(value).map(([name, amount]) => ({ name, value: amount })),
  }));

// Free text and contact details make meaningless chart categories.
const NOT_GROUPABLE = /(notes|remarks|description|challenges|plan|address|email|phone|title|number)$/i;

// Grouping candidates in the order a reader would want them: the dimension a
// report is really "about" first, an entity name only as a last resort. This is
// what stops a per-invoice report charting one bar per invoice number.
const GROUP_PRIORITY = [
  /department/i,
  /(aging|bucket)/i,
  /category/i,
  /status$/i,
  /stage/i,
  /source/i,
  /tier/i,
  /type$/i,
  /month/i,
  /(customer|account)_name$/i,
  /(employee|rep|owner|user)_name$/i,
  /name$/i,
];

/**
 * A chart derived from the rows, for the reports whose summary carries no
 * breakdown to draw (absences, leave balances, revenue by customer...).
 *
 * Rows are grouped by the most chart-worthy dimension present and measured by
 * the first money column, then any number column, and failing both by a row
 * count. Returns null when the rows offer nothing worth charting.
 */
export const deriveRowChart = (rows = [], columns = [], { limit = 12 } = {}) => {
  if (!rows.length || !columns.length) return null;

  const distinctCount = (key) => new Set(rows.map((row) => row?.[key] ?? '-')).size;
  const candidates = columns.filter((column) => column.format === 'text'
    && !NOT_GROUPABLE.test(column.key)
    && distinctCount(column.key) > 1);

  const groupColumn = GROUP_PRIORITY
    .map((pattern) => candidates.find((column) => pattern.test(column.key)))
    .find(Boolean);
  if (!groupColumn) return null;

  const valueColumn = columns.find((column) => column.format === 'currency')
    || columns.find((column) => column.format === 'number');

  const totals = new Map();
  rows.forEach((row) => {
    const name = String(row?.[groupColumn.key] ?? '-');
    const amount = valueColumn ? Number(row?.[valueColumn.key]) || 0 : 1;
    totals.set(name, (totals.get(name) || 0) + amount);
  });

  const data = [...totals.entries()]
    .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

  if (data.length < 2) return null;

  const measure = valueColumn ? valueColumn.title : 'Records';
  return {
    label: `${measure} by ${groupColumn.title}`,
    format: valueColumn ? valueColumn.format : 'number',
    truncated: totals.size > data.length,
    totalGroups: totals.size,
    data,
  };
};

/**
 * A trend over time, for the many reports whose rows carry a date (absences by
 * day, invoices by month). Days roll up to months once there are more points
 * than fit on an axis. Returns null when the rows have no usable date.
 */
export const deriveTrendChart = (rows = [], columns = [], { maxPoints = 45 } = {}) => {
  if (!rows.length) return null;

  const valueColumn = columns.find((column) => column.format === 'currency')
    || columns.find((column) => column.format === 'number');

  // Take the first date column that actually holds dates: a report can carry
  // several, and the leading one is often mostly 'N/A' (a conversion date, say).
  let dateColumn = null;
  let totals = new Map();
  for (const column of columns.filter((candidate) => candidate.format === 'date')) {
    const grouped = new Map();
    rows.forEach((row) => {
      const raw = String(row?.[column.key] ?? '').slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return;
      const amount = valueColumn ? Number(row?.[valueColumn.key]) || 0 : 1;
      grouped.set(raw, (grouped.get(raw) || 0) + amount);
    });
    if (grouped.size >= 3) {
      dateColumn = column;
      totals = grouped;
      break;
    }
  }
  if (!dateColumn) return null;

  let grouping = 'day';
  let points = [...totals.entries()];

  if (points.length > maxPoints) {
    grouping = 'month';
    const byMonth = new Map();
    points.forEach(([day, value]) => {
      const month = day.slice(0, 7);
      byMonth.set(month, (byMonth.get(month) || 0) + value);
    });
    points = [...byMonth.entries()];
  }

  const data = points
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }));

  return {
    label: `${valueColumn ? valueColumn.title : 'Records'} by ${grouping === 'month' ? 'month' : dateColumn.title}`,
    format: valueColumn ? valueColumn.format : 'number',
    grouping,
    data,
  };
};

/** Summary entries that are a plain list, e.g. 'departments_affected'. */
export const listSummaryEntries = (summary = {}) => Object.entries(summary || {})
  .filter(([, value]) => Array.isArray(value) && value.every((entry) => typeof entry !== 'object'))
  .map(([key, value]) => ({ key, label: humanizeKey(key), values: value }));
